/**
 * Recap ↔ offline bridge for typed AI suggestion rows.
 *
 * Reads the pending `pre_order_suggestions` / `pre_order_flow_suggestions`
 * rows from Dexie (via the `@stockline/web` AISync hooks the recap already
 * sits under) and exposes:
 *  - `maps` — targetId → adapted suggestion, fed to `projectPreOrders` so the
 *    render path sees the typed rows as `ai_suggestions[]` blocks;
 *  - `stampResolutions` — flips each resolved row's `acceptedAt` / `declinedAt`
 *    after the push-batch lands, the row-side counterpart of the column writes
 *    `resolveAiLocally` already produces.
 *
 * The recap stays offline-first: both reads (reactive Dexie queries) and the
 * resolve stamp (optimistic put + queued push) go through the existing sync
 * engine — no new network fetch in render.
 */

import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  usePreOrderFlowSuggestionMutations,
  usePreOrderFlowSuggestions,
  usePreOrderSuggestionMutations,
  usePreOrderSuggestions,
} from "@stockline/web";
import { useUserOptional } from "@/components/auth-provider";
import {
  buildSuggestionMaps,
  type SuggestionMaps,
} from "../services/aiSuggestionProjection";
import type {
  SuggestionResolution,
  SuggestionRowIndex,
} from "../services/aiResolveLocally/index";

export interface RecapAiSuggestions {
  maps: SuggestionMaps;
  /** targetId → sidecar row id, the lookup `resolveAiLocally` needs to report
   * which rows to stamp. Derived from `maps`. */
  rowIndex: SuggestionRowIndex;
  /** Stamp every resolved row's lifecycle timestamp. Synthetic add blocks
   * (no `id`) are skipped — those resolve through the main-row write. */
  stampResolutions: (resolutions: SuggestionResolution[]) => Promise<void>;
}

function toRowIndex(maps: SuggestionMaps): SuggestionRowIndex {
  const header = new Map<string, string[]>();
  for (const [targetId, adapted] of maps.header) {
    header.set(targetId, adapted.rowIds);
  }
  const flow = new Map<string, string[]>();
  for (const [targetId, adapted] of maps.flow) {
    flow.set(targetId, adapted.rowIds);
  }
  return { header, flow };
}

export function useRecapAiSuggestions(): RecapAiSuggestions {
  const { t } = useTranslation();
  const user = useUserOptional();
  const headerRows = usePreOrderSuggestions();
  const flowRows = usePreOrderFlowSuggestions();
  const { resolve: resolveHeader } = usePreOrderSuggestionMutations();
  const { resolve: resolveFlow } = usePreOrderFlowSuggestionMutations();

  const maps = useMemo(
    () => buildSuggestionMaps(headerRows, flowRows),
    [headerRows, flowRows],
  );
  const rowIndex = useMemo(() => toRowIndex(maps), [maps]);

  const stampResolutions = useCallback(
    async (resolutions: SuggestionResolution[]): Promise<void> => {
      const resolvedBy = user?.id ?? null;
      // Never-rejecting: stamping is a follow-up to a committed push-batch, so
      // a stamp failure must not abort the save/export (every call site fires
      // this as `void stampResolutions(...)`). But a silently-lost stamp
      // leaves the row pending → the already-applied proposal re-renders and
      // could be resolved twice. The only fallible step is the local Dexie
      // put — the outbound push rides the persistent change queue — so each
      // stamp is retried with backoff, and a final failure surfaces a toast
      // telling the user the proposal will re-render as already applied.
      const results = await Promise.allSettled(
        resolutions.map(async (r) => {
          if (!r.id) return;
          const stamp =
            r.decision === "accepted"
              ? { acceptedAt: new Date().toISOString(), resolvedBy }
              : { declinedAt: new Date().toISOString(), resolvedBy };
          const write = () =>
            r.kind === "header"
              ? resolveHeader(r.id as string, stamp)
              : resolveFlow(r.id as string, stamp);
          let lastErr: unknown;
          for (let attempt = 0; attempt < 3; attempt++) {
            try {
              await write();
              return;
            } catch (err) {
              lastErr = err;
              await new Promise((res) => setTimeout(res, 250 * (attempt + 1)));
            }
          }
          console.error(
            `[recap-ai] failed to stamp suggestion row ${r.id} (${r.kind}/${r.decision}); the proposal may re-render as pending until the next sync`,
            lastErr,
          );
          throw lastErr;
        }),
      );
      if (results.some((r) => r.status === "rejected")) {
        toast.error(
          t(
            "recap.ai.stampFailed",
            "Proposition déjà appliquée, mais son statut n'a pas pu être enregistré — elle peut réapparaître comme en attente.",
          ),
        );
      }
    },
    [resolveHeader, resolveFlow, user?.id, t],
  );

  return { maps, rowIndex, stampResolutions };
}
